import i18n from 'i18n/config';

import { DELTA_SUPPORT_URL } from './constants';

const STRATEGY_ANALYTICS_DURATION = {
  ONE_WEEK: '7d',
  ONE_MONTH: '30d',
  SIX_MONTHS: '180d',
} as const;

const ALLOWED_TRAILING_STOP_LOSS_RANGE = [5, 95]; // Percentage value
const DEFAULT_TRAILING_STOP_LOSS = 20; // Percentage value
const MAXIMUM_TRAILING_STOP_LOSS = 98; // Percentage value
const DEFAULT_AGGR_PNL_STOP_LOSS = 0; //  currency value

const POPUP_TTL = 5000; // 5 secs

const DEFAULT_OPTIONS_FEE_PER_TRADE = 0.01; // 0.01 percent of notional;

const CONTRACT_FILTER_TYPE = {
  FUTURES: 'Futures',
  OPTIONS: 'Options',
};

const COPY_TRADING_MODAL_VARIANTS = {
  COPY_TRADE: 'COPY_TRADE',
  ADD_FUNDS: 'ADD_FUNDS',
  EXIT_STRATEGY: 'EXIT_STRATEGY',
} as const;

const STRATEGY_TABLE_ACTIONS = {
  ADD_FUNDS: 'ADD_FUNDS',
  EXIT_STRATEGY: 'EXIT_STRATEGY',
  FORCE_EXIT: 'FORCE_EXIT',
  CANCEL_EXIT: 'CANCEL_EXIT',
} as const;

const STRATEGY_COPY_STATE = {
  TRADING: 'trading',
  WAITING_EXIT: 'waiting_exit',
  EXIT_IN_PROGRESS: 'exit_in_progress',
  EXITED: 'exited',
} as const;

const COPY_TRADING_ERROR_MESSAGES = {
  ALREADY_EXITED: 'already_exited',
  INSUFFICIENT_MARGIN: 'insufficient_margin',
  NO_ACTIVE_SUBSCRIPTION: 'no_active_subscription',
  NO_PENDING_EXIT_REQUEST: 'no_pending_exit_request',
  STRATEGY_FUNDS_DEPLOYED_LIMIT_BREACHED: 'strategy_funds_deployed_limit_breached',
  STRATEGY_SUBSCRIPTION_LIMIT_BREACHED: 'strategy_subscription_limit_breached',
  RISK_VALUE_GREATER_THAN_SUB_AMOUNT: 'risk_value_greater_than_sub_amount',
} as const;

const ERROR_MESSAGES = {
  [COPY_TRADING_ERROR_MESSAGES.ALREADY_EXITED]: 'Already exited!',
  [COPY_TRADING_ERROR_MESSAGES.STRATEGY_FUNDS_DEPLOYED_LIMIT_BREACHED]: i18n.t(
    'trading:algoMarketplace.addFundError.subscribedValueLimitBreach'
  ),
  [COPY_TRADING_ERROR_MESSAGES.STRATEGY_SUBSCRIPTION_LIMIT_BREACHED]: i18n.t(
    'trading:algoMarketplace.addFundError.subscriberLimitBreach'
  ),
  [COPY_TRADING_ERROR_MESSAGES.RISK_VALUE_GREATER_THAN_SUB_AMOUNT]:
    'Stop loss cannot be more than Subscribed Amount',
  DEFAULT: 'Something went wrong! Please try again.',
} as const;

const STRATEGY_FILTER_DEFAULT_OPTION_ID = 'all';

const API_STATUS = {
  IDLE: 'idle',
  FAILED: 'failed',
  LOADING: 'loading',
  SUCCESS: 'success',
} as const;

const EXIT_TYPE = {
  FORCE_EXIT: 'force_exit',
  NEUTRAL_EXIT: 'neutral_exit',
  STOP_LIMIT_BREACHED: 'stop_limit_breached',
  NONE: 'none',
} as const;

const SLIDER_OPTIONS = {
  MIN: 5,
  MAX: 95,
  STEP: 5,
};

const CURRENCY_PRECISION = 2;
const CURRENCY_PRECISION_FOR_POSITIONS_TOOLTIP = 4;

const OPEN_POSITIONS_TABLE_PREVIEW_LIMIT = 2;

const HOW_IT_WORKS_URL = `${DELTA_SUPPORT_URL}?categoryId=80000464981&folderId=80000727198&articleId=80001185498`;

const ALGO_MARKETPLACE_TERMS_AND_CONDITIONS_URL = `${DELTA_SUPPORT_URL}?categoryId=80000464981&folderId=80000727198&articleId=80001191587`;

const ALGO_DASHBOARD_ACTIVE_TAB = 'algo_dashboard_active_tab';
const ALGO_ACCOUNT_OVERVIEW_EYE_CLOSED = 'algo_account_overview_eye_closed';

const REGEX_FOR_TRAILING_STOP_LOSS = /^\d+(?:\.\d+)?$/;

const MIN_TOPUP_AMOUNT = 10;

const TIME_RANGE_ALL_OPTION = 'all';
const TIME_RANGE_BACKTEST_OPTION = 'backtest';

const LIVE_MODE_TIME_RANGES = [TIME_RANGE_ALL_OPTION, 7, 30, 180, 365] as const;

// For Live mode Tabs
const TIME_RANGE_TAB_LABELS = {
  [TIME_RANGE_ALL_OPTION]: 'All',
  7: '7D',
  30: '30D',
  180: '6M',
  365: '1Y',
};

const STRATEGY_MODES_TIME_RANGES = [
  TIME_RANGE_BACKTEST_OPTION,
  ...LIVE_MODE_TIME_RANGES,
] as const;

// For Filter Dropdown
const TIME_RANGE_FILTER_LABEL_MAPPING = {
  [TIME_RANGE_ALL_OPTION]: 'All',
  7: 'Last 7 Days',
  30: 'Last 30 Days',
  180: 'Last 6 Months',
  365: 'Last 1 Year',
  [TIME_RANGE_BACKTEST_OPTION]: 'Backtest',
};

const STRATEGY_MODES = {
  backtest: 'backtest',
  live: 'live',
} as const;

const EQUITY_EXAMPLE_VALUE = 100;

const STRATEGY_OVER_SUBSCRIPTION_CASES = {
  MAX_SUBSCRIBERS_LIMIT_REACHED: 'maxSubscribersLimitReached',
  MAX_SUBSCRIBERS_VALUE_REACHED: 'maxSubscribersValueReached',
} as const;

const OVER_SUBSCRIPTION_TEXTS = {
  [STRATEGY_OVER_SUBSCRIPTION_CASES.MAX_SUBSCRIBERS_LIMIT_REACHED]:
    'Strategy max subscribers limit reached',
  [STRATEGY_OVER_SUBSCRIPTION_CASES.MAX_SUBSCRIBERS_VALUE_REACHED]:
    'Strategy max subscribers value reached',
};

const RETURNS_LABEL = {
  [TIME_RANGE_BACKTEST_OPTION]: i18n.t('algoMarketplace:annualizedReturns'),
  [TIME_RANGE_ALL_OPTION]: i18n.t('algoMarketplace:allTimeReturns'),
};

// Contains time ranges that should be aggregated weekly by backend (for large intervals)
const WEEKLY_AGGREGATED_TIME_RANGES = [180, 365, TIME_RANGE_ALL_OPTION];

export {
  COPY_TRADING_MODAL_VARIANTS,
  STRATEGY_COPY_STATE,
  COPY_TRADING_ERROR_MESSAGES,
  STRATEGY_TABLE_ACTIONS,
  STRATEGY_FILTER_DEFAULT_OPTION_ID,
  STRATEGY_ANALYTICS_DURATION,
  API_STATUS,
  ERROR_MESSAGES,
  DEFAULT_TRAILING_STOP_LOSS,
  SLIDER_OPTIONS,
  HOW_IT_WORKS_URL,
  ALGO_DASHBOARD_ACTIVE_TAB,
  DEFAULT_AGGR_PNL_STOP_LOSS,
  ALLOWED_TRAILING_STOP_LOSS_RANGE,
  REGEX_FOR_TRAILING_STOP_LOSS,
  ALGO_ACCOUNT_OVERVIEW_EYE_CLOSED,
  CURRENCY_PRECISION,
  MAXIMUM_TRAILING_STOP_LOSS,
  POPUP_TTL,
  ALGO_MARKETPLACE_TERMS_AND_CONDITIONS_URL,
  EXIT_TYPE,
  DEFAULT_OPTIONS_FEE_PER_TRADE,
  CONTRACT_FILTER_TYPE,
  OPEN_POSITIONS_TABLE_PREVIEW_LIMIT,
  CURRENCY_PRECISION_FOR_POSITIONS_TOOLTIP,
  MIN_TOPUP_AMOUNT,
  TIME_RANGE_FILTER_LABEL_MAPPING,
  STRATEGY_MODES_TIME_RANGES,
  TIME_RANGE_TAB_LABELS,
  LIVE_MODE_TIME_RANGES,
  STRATEGY_MODES,
  TIME_RANGE_ALL_OPTION,
  EQUITY_EXAMPLE_VALUE,
  STRATEGY_OVER_SUBSCRIPTION_CASES,
  TIME_RANGE_BACKTEST_OPTION,
  OVER_SUBSCRIPTION_TEXTS,
  RETURNS_LABEL,
  WEEKLY_AGGREGATED_TIME_RANGES,
};
